import {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCheckbox,
    IonCol,
    IonContent,
    IonIcon,
    IonPage,
    IonRow,
    IonSearchbar,
} from "@ionic/react";
import Header from "../../../../components/Headers/Header";
import PageTitle from "../../../../components/PageTitle/PageTitle";
import React, { useContext, useState } from "react";
import { Barn, BarnMember, EventEntry, Horse, Invitation, Membership, Owner, Person, Rider, StablingRequest, Trainer } from "../../../../models";
import { Table } from "reactstrap";
import { getHorsesByBarnId, updateHorse } from "../../../../utilities/horse/Horse";
import { getRidersByBarnId, updateRider } from "../../../../utilities/rider/Rider";
import { getOwnersByBarnId, updateOwner } from "../../../../utilities/owner/Owner";
import { getTrainersByBarnId, updateTrainer } from "../../../../utilities/trainer/Trainer";
import { getBarnsByName, updateBarn } from "../../../../utilities/barn/Barn";
import Spinner from "../../../../components/Spinners/Spinner";
import ErrorAlert from "../../../../components/Errors/ErrorAlert";
import { getInvitationsByBarnId, updateInvitation } from "../../../../utilities/invitation/Invitation";
import { getEventEntriesByBarnId, updateEventEntry } from "../../../../utilities/eventEntry/EventEntry";
import { getStablingRequestsByBarnId, updateStablingRequest } from "../../../../utilities/stablingRequest/StablingRequest";
import { getMembershipsByBarnId, updateMembership } from "../../../../utilities/membership/Membership";
import { formatAddress } from "../../../../utilities/address/FormatAddress";
import { getPersonByPersonId } from "../../../../utilities/person/Person";
import { formatDisplayNameForUser } from "../../../../utilities/person/PersonNameFormat";
import moment from "moment";
import { checkmark } from "ionicons/icons";
import { UpdateBarnInput, UpdateBarnMemberInput, UpdateEventEntryInput, UpdateHorseInput, UpdateInvitationInput, UpdateMembershipInput, UpdateOwnerInput, UpdateRiderInput, UpdateStablingRequestInput, UpdateTrainerInput } from "../../../../API";
import { updateBarnMember } from "../../../../utilities/barnMember/BarnMember";
import { PersonContext } from "../../../../context/PersonContext";

interface FormattedBarn {
    id: string
    barnName: string
    description: string
    type: string
    address: string
    website: string
    phoneNumber: string
    ownerPersonId: string
    ownerPersonName: string
    lastUpdatedById: string
    lastUpdatedByName: string
    numberOfHorses: string
    horses: Horse[]
    numberOfRiders: string
    riders: Rider[]
    numberOfOwners: string
    owners: Owner[]
    numberOfTrainers: string
    trainers: Trainer[]
    numberOfBarnMembers: string
    barnMembers: (BarnMember | null)[]
    numberOfInvitations: string
    invitations: Invitation[]
    numberOfEntries: string
    eventEntries: EventEntry[]
    numberOfStablingRequests: string
    stablingRequests: StablingRequest[]
    numberOfMemberships: string
    memberships: Membership[]
    dateCreated: string
    dateLastUpdated: string
}

const BarnMergePage: React.FC = () => {
    const user = useContext(PersonContext);

    // Initial search for barns
    const [searchText, setSearchText] = useState<string>("");
    const [barnsFoundInSearch, setBarnsFoundInSearch] = useState<Barn[] | null | undefined>();
    const [selectedBarnsFromSearch, setSelectedBarnsFromSearch] = useState<Barn[] | null | undefined>();

    // Formatted barns
    const [formattedBarns, setFormattedBarns] = useState<FormattedBarn[] | null | undefined>();
    const [selectedFormattedBarn, setSelectedFormattedBarn] = useState<FormattedBarn | null | undefined>();

    // Basic state
    const [step, setStep] = useState<1 | 2 | 3>(1);
    const [isLoading, setIsLoading] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");
    const [error, setError] = useState("");
    const [mergeErrors, setMergeErrors] = useState<string[] | null | undefined>();

    const handleSearchInput = (text: string) => {
        setSearchText(text);
    }

    const handleSearchClick = async () => {
        setIsLoading(true);
        setError("");
        if (searchText && searchText !== "") {
            const barnQuery = await getBarnsByName(searchText);
            if (barnQuery.isSuccess) {
                const foundBarns: Barn[] = barnQuery.result;
                setBarnsFoundInSearch(prevBarns => {
                    if (prevBarns && prevBarns.length > 0) {
                        const combinedBarnList = prevBarns.concat(foundBarns);
                        return combinedBarnList;
                    } else {
                        return foundBarns;
                    }
                });
            } else {
                console.log("Error: ", barnQuery);
                setError("An error occurred with searching.");
            }
        } else {
            setError("Please include a valid search string.");
        }
        setIsLoading(false);
    }

    const handleClearTableClick = () => {
        setBarnsFoundInSearch(null);
        setSearchText("");
    }

    const handleCheckBoxClick = (barn: Barn, isTurningOn: boolean) => {
        if (isTurningOn) {
            setSelectedBarnsFromSearch(prev => {
                if (prev && prev.length > 0) {
                    const combined = prev.concat([barn]);
                    return combined;
                } else {
                    return [barn];
                }
            });
        } else {
            const updatedBarnList = selectedBarnsFromSearch?.filter(b => b.id === barn.id);
            setSelectedBarnsFromSearch(prev => updatedBarnList);
        }
    }

    const formatSingleBarn = async (barn: Barn) => {
        // Get Horses with this barn
        let horsesInBarn: Horse[] = [];
        const horseQuery = await getHorsesByBarnId(barn.id);
        if (horseQuery.isSuccess) {
            horsesInBarn = horseQuery.result;
        }

        // Get Riders with this barn
        let ridersInBarn: Rider[] = [];
        const riderQuery = await getRidersByBarnId(barn.id);
        if (riderQuery.isSuccess) {
            ridersInBarn = riderQuery.result;
        }

        // Get Owners with this barn
        let ownersInBarn: Owner[] = [];
        const ownerQuery = await getOwnersByBarnId(barn.id);
        if (ownerQuery.isSuccess) {
            ownersInBarn = ownerQuery.result;
        }

        // Get Trainer with this barn
        let trainersInBarn: Trainer[] = [];
        const trainerQuery = await getTrainersByBarnId(barn.id);
        if (trainerQuery.isSuccess) {
            trainersInBarn = trainerQuery.result;
        }

        // Get BarnMembers with this barn
        let barnMembers: (BarnMember | null)[] = barn.members || [];

        // Get Invitations with this barn
        let invitationsInBarn: Invitation[] = [];
        const invitationQuery = await getInvitationsByBarnId(barn.id);
        if (invitationQuery.isSuccess) {
            invitationsInBarn = invitationQuery.result;
        }

        // Get EventEntries with this barn
        let eventEntries: EventEntry[] = [];
        const entryQuery = await getEventEntriesByBarnId(barn.id);
        if (entryQuery.isSuccess) {
            eventEntries = entryQuery.result;
        }

        // Get StablingRequests with this barn
        let stablingRequests: StablingRequest[] = [];
        const stablingRequestQuery = await getStablingRequestsByBarnId(barn.id);
        if (stablingRequestQuery.isSuccess) {
            stablingRequests = stablingRequestQuery.result;
        }

        // Get Memberships with this barn
        let memberships: Membership[] = [];
        const membershipQuery = await getMembershipsByBarnId(barn.id);
        if (membershipQuery.isSuccess) {
            memberships = membershipQuery.result;
        }

        // Barn's Owner's Name
        let barnOwnerName = "";
        if (barn.ownerPersonId) {
            const ownerPersonQuery = await getPersonByPersonId(barn.ownerPersonId);
            if (ownerPersonQuery.isSuccess) {
                const ownerPerson: Person = ownerPersonQuery.result;
                barnOwnerName = formatDisplayNameForUser(ownerPerson);
            }
        }

        // Barn Last Updated By Name
        let barnLastUpdatedBy = "";
        if (barn.lastUpdatedBy) {
            const lastUpdatedByPersonQuery = await getPersonByPersonId(barn.lastUpdatedBy);
            if (lastUpdatedByPersonQuery.isSuccess) {
                const lastUpdatedByPerson: Person = lastUpdatedByPersonQuery.result;
                barnLastUpdatedBy = formatDisplayNameForUser(lastUpdatedByPerson);
            }
        }

        const formattedBarn: FormattedBarn = {
            id: barn.id,
            barnName: barn.name,
            description: barn.description || "",
            type: barn.type || "",
            address: barn.location ? formatAddress(barn.location) : "",
            website: barn.contact ? (barn.contact?.website || "") : "",
            phoneNumber: barn.contact ? (barn.contact?.work || "") : "",
            ownerPersonId: barn.ownerPersonId || "",
            ownerPersonName: barnOwnerName,
            lastUpdatedById: barn.lastUpdatedBy || "",
            lastUpdatedByName: barnLastUpdatedBy,
            numberOfHorses: horsesInBarn.length ? horsesInBarn.length.toString() : "",
            horses: horsesInBarn,
            numberOfRiders: ridersInBarn.length ? ridersInBarn.length.toString() : "",
            riders: ridersInBarn,
            numberOfOwners: ownersInBarn.length ? ownersInBarn.length.toString() : "",
            owners: ownersInBarn,
            numberOfTrainers: trainersInBarn.length ? trainersInBarn.length.toString() : "",
            trainers: trainersInBarn,
            numberOfBarnMembers: barnMembers.length ? barnMembers.length.toString() : "",
            barnMembers: barnMembers,
            numberOfInvitations: invitationsInBarn.length ? invitationsInBarn.length.toString() : "",
            invitations: invitationsInBarn,
            numberOfEntries: eventEntries.length ? eventEntries.length.toString() : "",
            eventEntries: eventEntries,
            numberOfStablingRequests: stablingRequests.length ? stablingRequests.length.toString() : "",
            stablingRequests: stablingRequests,
            numberOfMemberships: memberships.length ? memberships.length.toString() : "",
            memberships: memberships,
            dateCreated: moment(barn.createdOn).format("MM/DD/YYYY"),
            dateLastUpdated: moment(barn.updatedOn).format("MM/DD/YYYY"),
        }

        return formattedBarn;
    }

    const formatBarns = async (barns: Barn[]) => {
        setProgressMessage("");
        const newlyFormattedBarns: FormattedBarn[] = [];
        for (let i = 0; i < barns.length; i++) {
            const barn = barns[i];
            setProgressMessage(`Getting details about barn #${i+1} out of ${barns.length}`);
            const formattedBarn = await formatSingleBarn(barn);
            newlyFormattedBarns.push(formattedBarn);
        }
        setProgressMessage("Done");
        return newlyFormattedBarns;
    }

    const handleUseSelectedRowsClick = async () => {
        setStep(2);
        setIsLoading(true);
        setSearchText("");
        if (selectedBarnsFromSearch && selectedBarnsFromSearch.length > 0) {
            const newlyFormattedBarns = await formatBarns(selectedBarnsFromSearch);
            setFormattedBarns(newlyFormattedBarns);
        }
        setIsLoading(false);
    }

    const mergeBarnOneHorsesIntoBarnTwoHorses = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const horses = barnOne.horses;
        if (horses && horses.length > 0) {
            for (let i = 0; i < horses.length; i++) {
                const horse = horses[i];
                const updateInput: UpdateHorseInput = {
                    id: horse.id,
                    personId: horse.personId || null,
                    barnId: barnTwo.id,
                    barnName: barnTwo.barnName
                };
                const updateResult = await updateHorse(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating a horse: ", updateResult);
                    const errorMsg = "Could not update the horse: " + horse.id + " - " + horse.name + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneRidersIntoBarnTwoRiders = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const riders = barnOne.riders;
        if (riders && riders.length > 0) {
            for (let i = 0; i < riders.length; i++) {
                const rider = riders[i];
                const updateInput: UpdateRiderInput = {
                    id: rider.id,
                    name: rider.name,
                    personId: rider.personId || null,
                    barnId: barnTwo.id || null,
                    barnName: barnTwo.barnName || null,
                };
                const updateResult = await updateRider(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating a rider: ", updateResult);
                    const errorMsg = "Could not update the rider: " + rider.id + " - " + rider.name + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneOwnersIntoBarnTwoOwners = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const owners = barnOne.owners;
        if (owners && owners.length > 0) {
            for (let i = 0; i < owners.length; i++) {
                const owner = owners[i];
                const updateInput: UpdateOwnerInput = {
                    id: owner.id,
                    barnId: barnTwo.id
                };
                const updateResult = await updateOwner(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating an owner: ", updateResult);
                    const errorMsg = "Could not update the owner: " + owner.id + " - " + owner.name + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneTrainersIntoBarnTwoTrainers = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const trainers = barnOne.trainers;
        if (trainers && trainers.length > 0) {
            for (let i = 0; i < trainers.length; i++) {
                const trainer = trainers[i];
                const updateInput: UpdateTrainerInput = {
                    id: trainer.id,
                    barnId: barnTwo.id,
                    barnName: barnTwo.barnName
                };
                const updateResult = await updateTrainer(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating a trainer: ", updateResult);
                    const errorMsg = "Could not update the trainer: " + trainer.id + " - " + trainer.name + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneBarnMembersIntoBarnTwoBarnMembers = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const barnMembers = barnOne.barnMembers;
        if (barnMembers && barnMembers.length > 0) {
            for (let i = 0; i < barnMembers.length; i++) {
                const barnMember = barnMembers[i];
                if (barnMember) {
                    const updateInput: UpdateBarnMemberInput = {
                        id: barnMember.id,
                        barnId: barnTwo.id
                    };
                    const updateResult = await updateBarnMember(updateInput);
                    if (updateResult.isSuccess === false) {
                        console.log("An error occurred while updating a barn member: ", updateResult);
                        const errorMsg = "Could not update the barn member: " + barnMember.id + " - " + barnMember.name + " in barn: " + barnOne.id;
                        updateErrors.push(errorMsg);
                    }
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneInvitationsIntoBarnTwoInvitations = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const invitations = barnOne.invitations;
        if (invitations && invitations.length > 0) {
            for (let i = 0; i < invitations.length; i++) {
                const invitation = invitations[i];
                const updateInput: UpdateInvitationInput = {
                    id: invitation.id,
                    barnId: barnTwo.id
                };
                const updateResult = await updateInvitation(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating an invitation: ", updateResult);
                    const errorMsg = "Could not update the invitation: " + invitation.id + " - creator email: " + invitation.creatorEmail + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneEventEntriesIntoBarnTwoEventEntries = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const entries = barnOne.eventEntries;
        if (entries && entries.length > 0) {
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const updateInput: UpdateEventEntryInput = {
                    id: entry.id,
                    barnId: barnTwo.id,
                    barnName: barnTwo.barnName
                };
                const updateResult = await updateEventEntry(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating an event entry: ", updateResult);
                    const errorMsg = "Could not update the entry: " + entry.id + " - horse name: " + entry.horseName + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneStablingRequestsIntoBarnTwoStablingRequests = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const stablingRequests = barnOne.stablingRequests;
        if (stablingRequests && stablingRequests.length > 0) {
            for (let i = 0; i < stablingRequests.length; i++) {
                const stablingRequest = stablingRequests[i];
                const updateInput: UpdateStablingRequestInput = {
                    id: stablingRequest.id,
                    barnId: barnTwo.id
                };
                const updateResult = await updateStablingRequest(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating a stabling request: ", updateResult);
                    const errorMsg = "Could not update the stablingRequest: " + stablingRequest.id + " - event id: " + stablingRequest.eventId + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneMembershipsIntoBarnTwoMemberships = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const updateErrors: string[] = [];
        const memberships = barnOne.memberships;
        if (memberships && memberships.length > 0) {
            for (let i = 0; i < memberships.length; i++) {
                const membership = memberships[i];
                const updateInput: UpdateMembershipInput = {
                    id: membership.id,
                    barnId: barnTwo.id
                };
                const updateResult = await updateMembership(updateInput);
                if (updateResult.isSuccess === false) {
                    console.log("An error occurred while updating a membership: ", updateResult);
                    const errorMsg = "Could not update the membership: " + membership.id + " - " + membership.name + " in barn: " + barnOne.id;
                    updateErrors.push(errorMsg);
                }
            }
        }
        return updateErrors;
    }

    const mergeBarnOneIntoBarnTwo = async (barnOne: FormattedBarn, barnTwo: FormattedBarn) => {
        const horseMergeErrors = await mergeBarnOneHorsesIntoBarnTwoHorses(barnOne, barnTwo);
        const riderMergeErrors = await mergeBarnOneRidersIntoBarnTwoRiders(barnOne, barnTwo);
        const ownerMergeErrors = await mergeBarnOneOwnersIntoBarnTwoOwners(barnOne, barnTwo);
        const trainerMergeErrors = await mergeBarnOneTrainersIntoBarnTwoTrainers(barnOne, barnTwo);
        const barnMemberMergeErrors = await mergeBarnOneBarnMembersIntoBarnTwoBarnMembers(barnOne, barnTwo);
        const invitationMergeErrors = await mergeBarnOneInvitationsIntoBarnTwoInvitations(barnOne, barnTwo);
        const eventEntryMergeErrors = await mergeBarnOneEventEntriesIntoBarnTwoEventEntries(barnOne, barnTwo);
        const stablingRequestsMergeErrors = await mergeBarnOneStablingRequestsIntoBarnTwoStablingRequests(barnOne, barnTwo);
        const membershipMergeErrors = await mergeBarnOneMembershipsIntoBarnTwoMemberships(barnOne, barnTwo);

        const errors: string[] = [
            ...horseMergeErrors,
            ...riderMergeErrors,
            ...ownerMergeErrors,
            ...trainerMergeErrors,
            ...barnMemberMergeErrors,
            ...invitationMergeErrors,
            ...eventEntryMergeErrors,
            ...stablingRequestsMergeErrors,
            ...membershipMergeErrors
        ];

        const updateBarnInput: UpdateBarnInput = {
            id: barnOne.id,
            lastUpdatedBy: user.id
        };
        const updateBarnResult = await updateBarn(updateBarnInput);
        if (updateBarnResult.isSuccess === false) {
            const errorMsg = "Could not update the barn: " + barnOne.id + " - " + barnOne.barnName;
            errors.push(errorMsg);
        }

        return errors;
    }

    const handleMergeClick = async () => {
        setStep(3);
        setProgressMessage("");
        setIsLoading(true);
        setError("");
        setMergeErrors(null);
        if (selectedFormattedBarn && formattedBarns) {
            const errors: string[] = [];

            // First, handle the merges
            for (let i = 0; i < formattedBarns.length; i++) {
                const barnOne = formattedBarns[i];
                setProgressMessage(`Merging barn #${i+1} out of ${(formattedBarns.length - 1)} into the selected barn.`);
                if (barnOne.id !== selectedFormattedBarn.id) {
                    const mergeErrors = await mergeBarnOneIntoBarnTwo(barnOne, selectedFormattedBarn);
                    errors.push(...mergeErrors);
                }
            }

            // Next, re-format the selected barns to ensure the merge was a success
            if (selectedBarnsFromSearch && selectedBarnsFromSearch.length > 0) {
                const newlyFormattedBarns = await formatBarns(selectedBarnsFromSearch);
                setFormattedBarns(newlyFormattedBarns);
            }

            setMergeErrors(errors);
        } else {
            setError("No barn was selected as the main barn for the merge.");
        }
        setIsLoading(false);
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title="Barn Merge" />
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            <IonCardTitle>Barn Merge Page</IonCardTitle>
                            <IonCardContent>
                                {error && <ErrorAlert error={error} width="12" />}

                                {/* User will search for barns and select the ones that are duplicates of one another. */}
                                {step === 1 && (
                                    <>
                                        <IonRow>
                                            <IonCol>
                                                <p>Step 1: Find all barns in the database that you need to merge.</p>
                                                <p>Search by Barn Name requires the name to be an exact match (case and white space senstive). You can search multiple times in order to build up a list of barns.</p>
                                            </IonCol>
                                        </IonRow>
                                        {isLoading ?
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol size="10" className="ion-text-center">
                                                    <Spinner />
                                                    <p>Getting any matching barns from the database.</p>
                                                </IonCol>
                                            </IonRow>
                                            :
                                            <>
                                                <IonRow className="ion-align-items-center">
                                                    <IonCol size="10">
                                                        <IonSearchbar value={searchText} placeholder="Search by Barn Name" onIonChange={e => handleSearchInput(e.detail.value!)} color="white"></IonSearchbar>
                                                    </IonCol>
                                                    <IonCol size="2">
                                                        <IonButton onClick={() => {handleSearchClick();}}>
                                                            Search
                                                        </IonButton>
                                                    </IonCol>
                                                </IonRow>
                                                {(barnsFoundInSearch && barnsFoundInSearch.length > 0) ?
                                                    <>
                                                        <IonRow className="ion-justify-content-start">
                                                            <IonCol size="6" className="ion-text-left">
                                                                {(selectedBarnsFromSearch && selectedBarnsFromSearch.length > 0) && (
                                                                    <IonButton color="success" onClick={() => handleUseSelectedRowsClick()}>
                                                                        Use Selected Rows
                                                                    </IonButton>
                                                                )}
                                                            </IonCol>
                                                            <IonCol size="4" className="ion-text-right">
                                                                <IonButton color="danger" onClick={() => handleClearTableClick()}>
                                                                    Clear Table
                                                                </IonButton>
                                                            </IonCol>
                                                        </IonRow>
                                                        <Table responsive>
                                                            <thead>
                                                                <tr>
                                                                    <th>Select</th>
                                                                    <th>Id</th>
                                                                    <th>Name</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {barnsFoundInSearch.map((barn, index) => (
                                                                    <tr key={index}>
                                                                        <td>
                                                                            <IonCheckbox onIonChange={e => handleCheckBoxClick(barn, e.detail.checked)} />
                                                                        </td>
                                                                        <td>{barn.id}</td>
                                                                        <td>{barn.name}</td>
                                                                    </tr>
                                                            ))}
                                                            </tbody>
                                                        </Table>
                                                    </>
                                                    :
                                                    <p></p>
                                                }
                                            </>
                                        }
                                    </>
                                )}

                                {/* Show more details for all of the selected barns */}
                                {step === 2 && (
                                    <>
                                        <IonRow>
                                            <IonCol>
                                                <p>Step 2: Review the details of the selected barns.</p>
                                                <p>Decide which barn should be the main barn that all other barns details will merge into.</p>
                                            </IonCol>
                                        </IonRow>
                                        {isLoading ?
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol size="10" className="ion-text-center">
                                                    <Spinner />
                                                    <p>{progressMessage || "Loading ..."}</p>
                                                </IonCol>
                                            </IonRow>
                                            :
                                            <>
                                                {(formattedBarns && formattedBarns.length > 0) ?
                                                    <>
                                                        <IonRow className="ion-justify-content-center">
                                                            <IonCol size="12" className="ion-text-center">
                                                                {selectedFormattedBarn && ( 
                                                                    <>
                                                                        <h2>Would you like to use the selected barn? All of the other barns will be merged into the selected barn.</h2>
                                                                        <IonButton color="success" onClick={() => handleMergeClick()}>
                                                                            Use Selected Barn
                                                                        </IonButton>
                                                                    </>
                                                                )}
                                                            </IonCol>
                                                        </IonRow>
                                                        <Table responsive>
                                                            <thead>
                                                                <tr>
                                                                    <th>Select</th>
                                                                    <th>Id</th>
                                                                    <th>Name</th>
                                                                    <th>Description</th>
                                                                    <th>Type</th>
                                                                    <th>Date Created</th>
                                                                    <th>Date Last Updated</th>
                                                                    <th>Address</th>
                                                                    <th>Website</th>
                                                                    <th>Phone Number</th>
                                                                    <th>Owner Person Id</th>
                                                                    <th>Owner Name</th>
                                                                    <th>Last Updated By Id</th>
                                                                    <th>Last Updated By Name</th>
                                                                    <th>Number of Horses</th>
                                                                    <th>Horses</th>
                                                                    <th>Number of Riders</th>
                                                                    <th>Riders</th>
                                                                    <th>Number of Owners</th>
                                                                    <th>Owners</th>
                                                                    <th>Number of Trainers</th>
                                                                    <th>Trainers</th>
                                                                    <th>Number of Barn Members</th>
                                                                    <th>Barn Members</th>
                                                                    <th>Number of Invitations</th>
                                                                    <th>Invites</th>
                                                                    <th>Number of Entries</th>
                                                                    <th>Entries</th>
                                                                    <th>Number of Stabling Requests</th>
                                                                    <th>S.R.</th>
                                                                    <th>Number of Memberships</th>
                                                                    <th>Memberships</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {formattedBarns.map((formattedBarn, i) => (
                                                                    <tr key={i}>
                                                                        <td onClick={() => setSelectedFormattedBarn(formattedBarn)}>
                                                                            <IonButton color={formattedBarn.id === selectedFormattedBarn?.id ? "success" : "light"}>
                                                                                {formattedBarn.id === selectedFormattedBarn?.id ? <IonIcon icon={checkmark} /> : "O"}
                                                                            </IonButton>
                                                                        </td>
                                                                        <td>{formattedBarn.id}</td>
                                                                        <td>{formattedBarn.barnName}</td>
                                                                        <td>{formattedBarn.description}</td>
                                                                        <td>{formattedBarn.type}</td>
                                                                        <td>{formattedBarn.dateCreated}</td>
                                                                        <td>{formattedBarn.dateLastUpdated}</td>
                                                                        <td>{formattedBarn.address}</td>
                                                                        <td>{formattedBarn.website}</td>
                                                                        <td>{formattedBarn.phoneNumber}</td>
                                                                        <td>{formattedBarn.ownerPersonId}</td>
                                                                        <td>{formattedBarn.ownerPersonName}</td>
                                                                        <td>{formattedBarn.lastUpdatedById}</td>
                                                                        <td>{formattedBarn.lastUpdatedByName}</td>
                                                                        <td>{formattedBarn.numberOfHorses}</td>
                                                                        <td>{formattedBarn.horses ? formattedBarn.horses.map((horse, index) => (<p key={index}>{horse.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfRiders}</td>
                                                                        <td>{formattedBarn.riders ? formattedBarn.riders.map((rider, index) => (<p key={index}>{rider.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfOwners}</td>
                                                                        <td>{formattedBarn.owners ? formattedBarn.owners.map((owner, index) => (<p key={index}>{owner.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfTrainers}</td>
                                                                        <td>{formattedBarn.trainers ? formattedBarn.trainers.map((trainer, index) => (<p key={index}>{trainer.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfBarnMembers}</td>
                                                                        <td></td>
                                                                        <td>{formattedBarn.numberOfInvitations}</td>
                                                                        <td>{formattedBarn.invitations ? formattedBarn.invitations.map((invite, index) => (<p key={index}>{invite.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfEntries}</td>
                                                                        <td>{formattedBarn.eventEntries ? formattedBarn.eventEntries.map((entry, index) => (<p key={index}>{entry.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfStablingRequests}</td>
                                                                        <td>{formattedBarn.stablingRequests ? formattedBarn.stablingRequests.map((stablingRequest, index) => (<p key={index}>{stablingRequest.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfMemberships}</td>
                                                                        <td>{formattedBarn.memberships ? formattedBarn.memberships.map((mem, index) => (<p key={index}>{mem.organizationName};</p>)): ""}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </>
                                                    :
                                                    <p></p>
                                                }
                                            </>
                                        }
                                    </>
                                )}

                                {/* Show more details for all of the selected barns */}
                                {step === 3 && (
                                    <>
                                        <IonRow>
                                            <IonCol>
                                                <p>Step 3: Review the merged barns.</p>
                                                <p>If you see any issues, you can start this process over again.</p>
                                            </IonCol>
                                        </IonRow>
                                        {(mergeErrors && mergeErrors.length > 0) && (
                                            <>
                                                {mergeErrors.map((mergeError, j) => (
                                                    <IonRow key={j}>
                                                        <ErrorAlert error={mergeError} width="12" />
                                                    </IonRow>
                                                ))}
                                            </>
                                        )}
                                        {isLoading ?
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol size="10" className="ion-text-center">
                                                    <Spinner />
                                                    <p>{progressMessage || "Loading ..."}</p>
                                                </IonCol>
                                            </IonRow>
                                            :
                                            <>
                                                {(formattedBarns && formattedBarns.length > 0) ?
                                                    <>
                                                        <Table responsive>
                                                            <thead>
                                                                <tr>
                                                                    <th>Id</th>
                                                                    <th>Name</th>
                                                                    <th>Description</th>
                                                                    <th>Type</th>
                                                                    <th>Date Created</th>
                                                                    <th>Date Last Updated</th>
                                                                    <th>Address</th>
                                                                    <th>Website</th>
                                                                    <th>Phone Number</th>
                                                                    <th>Owner Person Id</th>
                                                                    <th>Owner Name</th>
                                                                    <th>Last Updated By Id</th>
                                                                    <th>Last Updated By Name</th>
                                                                    <th>Number of Horses</th>
                                                                    <th>Horses</th>
                                                                    <th>Number of Riders</th>
                                                                    <th>Riders</th>
                                                                    <th>Number of Owners</th>
                                                                    <th>Owners</th>
                                                                    <th>Number of Trainers</th>
                                                                    <th>Trainers</th>
                                                                    <th>Number of Barn Members</th>
                                                                    <th>Barn Members</th>
                                                                    <th>Number of Invitations</th>
                                                                    <th>Invites</th>
                                                                    <th>Number of Entries</th>
                                                                    <th>Entries</th>
                                                                    <th>Number of Stabling Requests</th>
                                                                    <th>S.R.</th>
                                                                    <th>Number of Memberships</th>
                                                                    <th>Memberships</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {formattedBarns.map((formattedBarn, i) => (
                                                                    <tr key={i}>
                                                                        <td>{formattedBarn.id}</td>
                                                                        <td>{formattedBarn.barnName}</td>
                                                                        <td>{formattedBarn.description}</td>
                                                                        <td>{formattedBarn.type}</td>
                                                                        <td>{formattedBarn.dateCreated}</td>
                                                                        <td>{formattedBarn.dateLastUpdated}</td>
                                                                        <td>{formattedBarn.address}</td>
                                                                        <td>{formattedBarn.website}</td>
                                                                        <td>{formattedBarn.phoneNumber}</td>
                                                                        <td>{formattedBarn.ownerPersonId}</td>
                                                                        <td>{formattedBarn.ownerPersonName}</td>
                                                                        <td>{formattedBarn.lastUpdatedById}</td>
                                                                        <td>{formattedBarn.lastUpdatedByName}</td>
                                                                        <td>{formattedBarn.numberOfHorses}</td>
                                                                        <td>{formattedBarn.horses ? formattedBarn.horses.map((horse, index) => (<p key={index}>{horse.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfRiders}</td>
                                                                        <td>{formattedBarn.riders ? formattedBarn.riders.map((rider, index) => (<p key={index}>{rider.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfOwners}</td>
                                                                        <td>{formattedBarn.owners ? formattedBarn.owners.map((owner, index) => (<p key={index}>{owner.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfTrainers}</td>
                                                                        <td>{formattedBarn.trainers ? formattedBarn.trainers.map((trainer, index) => (<p key={index}>{trainer.name};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfBarnMembers}</td>
                                                                        <td></td>
                                                                        <td>{formattedBarn.numberOfInvitations}</td>
                                                                        <td>{formattedBarn.invitations ? formattedBarn.invitations.map((invite, index) => (<p key={index}>{invite.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfEntries}</td>
                                                                        <td>{formattedBarn.eventEntries ? formattedBarn.eventEntries.map((entry, index) => (<p key={index}>{entry.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfStablingRequests}</td>
                                                                        <td>{formattedBarn.stablingRequests ? formattedBarn.stablingRequests.map((stablingRequest, index) => (<p key={index}>{stablingRequest.id};</p>)): ""}</td>
                                                                        <td>{formattedBarn.numberOfMemberships}</td>
                                                                        <td>{formattedBarn.memberships ? formattedBarn.memberships.map((mem, index) => (<p key={index}>{mem.organizationName};</p>)): ""}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </>
                                                    :
                                                    <p></p>
                                                }
                                            </>
                                        }
                                    </>
                                )}
                            </IonCardContent>
                        </IonCard>
                    </IonCol>
                </IonRow>
            </IonContent>
        </IonPage>
    );
};

export default BarnMergePage;